// Apolo IDE — BranchMenu (Pedro 2026-06-25, sprint cursor-strip-3).
//
// Drop-up que abre quando o usuário clica no chip de branch (ex:
// feat/learn-loop-opens-pr) na GitConnectionBar. Lista REAL de branches do
// repo via window.apolo.git.listBranches + ação "Nova branch…" com input.
// Trocar branch via git switch (window.apolo.git.checkoutBranch).
//
// IPC real, sem mockup:
//   - apolo:git:list-branches  → { branches: [{name, isCurrent, isRemote, date}], current }
//   - apolo:git:checkout-branch → switch real
//   - apolo:git:create-branch   → switch -c real
//
// Degrada se não houver electron bridge (web/jsdom) — mostra aviso, nada
// crasha. Componente presentacional + IPC apenas; reload do estado git fica
// com o caller via onAfterSwitch.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GitBranch, Plus, Loader2, X, Lock } from 'lucide-react';
import { hasElectronBridge } from '../../lib/electronBridge.js';

const PROTECTED = new Set(['main', 'master', 'production', 'release', 'prod']);

function relativeAge(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = Date.now() - t;
  if (diff < 60_000) return 'agora';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h`;
  return `${Math.round(diff / 86_400_000)}d`;
}

export default function BranchMenu({
  open,
  onClose,
  root,
  currentBranch,
  onAfterSwitch,
  anchorClassName = 'absolute bottom-full left-0 z-50 mb-2',
}) {
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [newBranch, setNewBranch] = useState('');
  const [busy, setBusy] = useState(false);
  const menuRef = useRef(null);
  const filterRef = useRef(null);

  // Fetch real branches when opening.
  useEffect(() => {
    if (!open) return;
    if (!hasElectronBridge() || !window.apolo?.git?.listBranches) {
      setError('Trocar de branch só funciona no app Electron.');
      return;
    }
    setLoading(true);
    setError(null);
    window.apolo.git
      .listBranches({ root, includeRemote: true })
      .then((res) => {
        if (!res?.ok) {
          setError(res?.error || 'Não foi possível listar branches.');
          setBranches([]);
        } else {
          setBranches(Array.isArray(res.branches) ? res.branches : []);
        }
      })
      .catch((e) => setError(e?.message || 'Falha ao listar branches.'))
      .finally(() => setLoading(false));
  }, [open, root]);

  // Auto-focus filter on open. Click-outside + Esc to close.
  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => filterRef.current?.focus());
    function onDoc(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose?.();
    }
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let xs = branches;
    if (q) xs = xs.filter((b) => b.name.toLowerCase().includes(q));
    return xs;
  }, [branches, filter]);

  const handleSwitch = useCallback(
    async (name) => {
      if (!name || name === currentBranch || busy) return;
      setBusy(true);
      setError(null);
      try {
        const res = await window.apolo.git.checkoutBranch({ root, branch: name });
        if (!res?.ok) {
          if (res?.reason === 'dirty_worktree') {
            setError('Tem mudanças não-commitadas. Faça commit/stash e tente de novo.');
          } else {
            setError(res?.error || res?.detail || 'Falha ao trocar de branch.');
          }
          return;
        }
        onAfterSwitch?.(res.branch || name);
        onClose?.();
      } finally {
        setBusy(false);
      }
    },
    [root, currentBranch, busy, onAfterSwitch, onClose],
  );

  const handleCreate = useCallback(async () => {
    const name = newBranch.trim();
    if (!name || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await window.apolo.git.createBranch({ root, branch: name });
      if (!res?.ok) {
        if (res?.reason === 'already_exists') {
          setError(`Branch "${name}" já existe.`);
        } else {
          setError(res?.error || res?.detail || 'Falha ao criar branch.');
        }
        return;
      }
      onAfterSwitch?.(res.branch || name);
      onClose?.();
    } finally {
      setBusy(false);
    }
  }, [root, newBranch, busy, onAfterSwitch, onClose]);

  if (!open) return null;
  const electron = hasElectronBridge();

  return (
    <div
      ref={menuRef}
      role="dialog"
      aria-label="Trocar de branch"
      data-testid="branch-menu"
      className={
        anchorClassName +
        ' min-w-[280px] max-w-[360px] rounded-md border border-apolo-borderSubtle bg-apolo-surface1 p-1 shadow-xl backdrop-blur-md'
      }
    >
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <span className="text-[10.5px] uppercase tracking-wide text-apolo-textTertiary">
          Trocar de branch
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="inline-flex h-4 w-4 items-center justify-center rounded text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
        >
          <X size={11} aria-hidden="true" />
        </button>
      </div>

      {electron && !creating && (
        <input
          ref={filterRef}
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Buscar branch…"
          aria-label="Filtrar branches"
          data-testid="branch-menu-filter"
          className="mx-2 mb-1 block w-[calc(100%-1rem)] rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1 text-[12px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none focus:ring-1 focus:ring-apolo-accent/40"
        />
      )}

      {loading && (
        <p className="px-2 py-1.5 text-[11px] text-apolo-textTertiary">
          <Loader2 size={11} aria-hidden="true" className="mr-1 inline-block animate-spin" />
          Lendo branches…
        </p>
      )}

      {!loading && electron && !creating && (
        <ul className="flex max-h-[280px] flex-col overflow-y-auto">
          {filtered.length === 0 ? (
            <li className="px-2 py-1.5 text-[11px] text-apolo-textTertiary">
              Nenhuma branch encontrada.
            </li>
          ) : (
            filtered.map((b) => {
              const isCur = b.isCurrent || b.name === currentBranch;
              const isProtected = PROTECTED.has(b.name);
              return (
                <li key={b.name}>
                  <button
                    type="button"
                    onClick={() => handleSwitch(b.name)}
                    disabled={isCur || busy}
                    data-testid="branch-menu-item"
                    title={isCur ? 'Branch atual' : `Trocar para ${b.name}`}
                    className={
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] transition disabled:cursor-not-allowed ' +
                      (isCur
                        ? 'bg-apolo-surface3 text-apolo-textPrimary'
                        : 'text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary')
                    }
                  >
                    {isProtected ? (
                      <Lock size={11} aria-hidden="true" className="shrink-0 text-apolo-textTertiary" />
                    ) : (
                      <GitBranch size={11} aria-hidden="true" className="shrink-0 text-apolo-textTertiary" />
                    )}
                    <span className="truncate font-mono">{b.name}</span>
                    {b.isRemote && (
                      <span className="shrink-0 rounded border border-apolo-borderSubtle px-1 text-[9.5px] font-mono text-apolo-textTertiary">
                        remote
                      </span>
                    )}
                    {b.date && (
                      <span className="ml-auto shrink-0 font-mono text-[10px] text-apolo-textTertiary">
                        {relativeAge(b.date)}
                      </span>
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}

      {electron && (
        <>
          <div className="my-1 h-px bg-apolo-borderSubtle" aria-hidden="true" />
          {!creating ? (
            <button
              type="button"
              onClick={() => {
                setCreating(true);
                setNewBranch(filter.trim());
                requestAnimationFrame(() => filterRef.current?.focus());
              }}
              data-testid="branch-menu-new"
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-apolo-textPrimary transition hover:bg-apolo-surface2"
            >
              <Plus size={13} aria-hidden="true" className="text-apolo-textSecondary" />
              <span>Nova branch…</span>
            </button>
          ) : (
            <form
              className="flex items-center gap-1 px-2 py-1.5"
              onSubmit={(e) => {
                e.preventDefault();
                handleCreate();
              }}
            >
              <Plus size={13} aria-hidden="true" className="shrink-0 text-apolo-textSecondary" />
              <input
                autoFocus
                type="text"
                value={newBranch}
                onChange={(e) => setNewBranch(e.target.value)}
                placeholder="nome-da-branch"
                aria-label="Nome da nova branch"
                data-testid="branch-menu-new-input"
                className="block flex-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1 text-[12px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none focus:ring-1 focus:ring-apolo-accent/40"
              />
              <button
                type="submit"
                disabled={!newBranch.trim() || busy}
                className="inline-flex h-7 shrink-0 items-center justify-center rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 text-[11px] text-apolo-textPrimary transition hover:bg-apolo-surface3 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Criar
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setNewBranch('');
                  setError(null);
                }}
                className="inline-flex h-7 shrink-0 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-surface2"
                aria-label="Cancelar criação"
              >
                <X size={12} aria-hidden="true" />
              </button>
            </form>
          )}
        </>
      )}

      {!electron && (
        <p className="px-2 py-1.5 text-[10.5px] text-apolo-textTertiary">
          Trocar de branch só funciona no app Electron.
        </p>
      )}
      {error && (
        <p role="alert" className="px-2 py-1.5 text-[10.5px] text-apolo-error">{error}</p>
      )}
    </div>
  );
}
